import Material from './Material.js';

/**
 * Factory class for creating and managing materials
 */
export default class MaterialFactory {
  constructor() {
    // Material library - predefined materials
    this.library = {
      // Basic colors
      'red': Material.color('#e74c3c'),
      'blue': Material.color('#3498db'),
      'green': Material.color('#2ecc71'),
      'yellow': Material.color('#f1c40f'),
      'purple': Material.color('#9b59b6'),
      'orange': Material.color('#e67e22'),
      'white': Material.color('#ffffff'),
      'black': Material.color('#000000'),
      
      // Transparent materials
      'glass': Material.color('rgba(200, 230, 255, 0.5)', 0.5),
      'redGlass': Material.color('rgba(231, 76, 60, 0.5)', 0.5),
      'blueGlass': Material.color('rgba(52, 152, 219, 0.5)', 0.5),
      'greenGlass': Material.color('rgba(46, 204, 113, 0.5)', 0.5),
      
      // Gradients
      'sunset': Material.gradient('linear', ['#ff9966', '#ff5e62'], 'to bottom'),
      'ocean': Material.gradient('linear', ['#2980b9', '#6dd5fa', '#ffffff'], 'to top'),
      'rainbow': Material.gradient('linear', 
        ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'], 
        'to right'),
      
      // Patterns
      'blueStripes': Material.pattern('stripes', {
        colors: ['#4285f4', '#34a853'],
        direction: '45deg',
        size: '10px'
      }),
      'grid': Material.pattern('grid', {
        gridColor: '#ddd',
        backgroundColor: '#fff',
        gridSize: '10px',
        lineWidth: '1px'
      }),
      'dots': Material.pattern('dots', {
        backgroundColor: '#fff',
        dotColor: '#000',
        dotSize: '5px',
        spacing: '15px'
      })
    };
    
    // Cache for dynamically created materials
    this.cache = new Map();
  }
  
  /**
   * Get a material from the library
   * @param {string} name - Material name
   * @return {Material} The material
   */
  get(name) {
    if (this.library[name]) {
      // Return a clone to prevent modifying the original
      return this.cloneMaterial(this.library[name]);
    }
    return null;
  }
  
  /**
   * Add a material to the library
   * @param {string} name - Material name
   * @param {Material} material - The material
   */
  add(name, material) {
    this.library[name] = material;
  }
  
  /**
   * Create a color material
   * @param {string} color - Color value
   * @param {number} opacity - Opacity (0-1)
   * @return {Material} The created material
   */
  createColor(color, opacity = 1.0) {
    const key = `color:${color}:${opacity}`;
    
    if (!this.cache.has(key)) {
      this.cache.set(key, Material.color(color, opacity));
    }
    
    return this.cloneMaterial(this.cache.get(key));
  }
  
  /**
   * Create a texture material
   * @param {string} url - Texture URL
   * @param {number} opacity - Opacity (0-1)
   * @return {Material} The created material
   */
  createTexture(url, opacity = 1.0) {
    const key = `texture:${url}:${opacity}`;
    
    if (!this.cache.has(key)) {
      this.cache.set(key, Material.texture(url, opacity));
    }
    
    return this.cloneMaterial(this.cache.get(key));
  }
  
  /**
   * Create a video material
   * @param {string} url - Video URL
   * @param {number} opacity - Opacity (0-1)
   * @return {Material} The created material
   */
  createVideo(url, opacity = 1.0) {
    // Videos are not cached since they're dynamic
    return Material.video(url, opacity);
  }
  
  /**
   * Create a gradient material
   * @param {string} type - 'linear' or 'radial'
   * @param {string[]} colors - Array of colors
   * @param {string} direction - Direction or shape
   * @param {number} opacity - Opacity (0-1)
   * @return {Material} The created material
   */
  createGradient(type, colors, direction, opacity = 1.0) {
    const key = `gradient:${type}:${colors.join(',')}:${direction}:${opacity}`;
    
    if (!this.cache.has(key)) {
      this.cache.set(key, Material.gradient(type, colors, direction, opacity));
    }
    
    return this.cloneMaterial(this.cache.get(key));
  }
  
  /**
   * Create a pattern material
   * @param {string} type - Pattern type
   * @param {Object} options - Pattern options
   * @param {number} opacity - Opacity (0-1)
   * @return {Material} The created material
   */
  createPattern(type, options, opacity = 1.0) {
    const key = `pattern:${type}:${JSON.stringify(options)}:${opacity}`;
    
    if (!this.cache.has(key)) {
      this.cache.set(key, Material.pattern(type, options, opacity));
    }
    
    return this.cloneMaterial(this.cache.get(key));
  }
  
  /**
   * Create a text material
   * @param {string} text - The text
   * @param {string} backgroundColor - Background color
   * @param {string} textColor - Text color
   * @param {string} textSize - Text size
   * @return {Material} The created material
   */
  createText(text, backgroundColor = '#ffffff', textColor = '#000000', textSize = '12px') {
    const material = Material.color(backgroundColor);
    material.text = text;
    material.textColor = textColor;
    material.textSize = textSize;
    return material;
  }
  
  /**
   * Clone a material
   * @param {Material} material - The material to clone
   * @return {Material} The cloned material
   */
  cloneMaterial(material) {
    // Create a new material with the same properties
    const clone = new Material({
      type: material.type,
      color: material.color,
      opacity: material.opacity,
      texture: material.texture,
      video: material.video,
      text: material.text,
      textColor: material.textColor,
      textSize: material.textSize,
      textFont: material.textFont,
      gradient: material.gradient ? { ...material.gradient } : null,
      pattern: material.pattern ? { ...material.pattern } : null,
      borderColor: material.borderColor,
      borderWidth: material.borderWidth,
      className: material.className
    });
    
    return clone;
  }
  
  /**
   * Clear the material cache
   */
  clearCache() {
    this.cache.clear();
  }
}